import { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { installAppModal } from '../design-system';

const LAST_INSTALL_PROPOSAL_KEY = 'pwa_last_install_proposal_date';
const DAYS_BETWEEN_PROPOSALS = 2;

export const usePwaInstalled = (): [boolean, (installed: boolean) => void] => {
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const check = () => {
      const standalone =
        window.matchMedia('(display-mode: standalone)').matches ||
        (window as any).navigator.standalone === true;

      setInstalled(standalone);
    };

    check();

    window.addEventListener('appinstalled', () => setInstalled(true));
    window.matchMedia('(display-mode: standalone)').addEventListener('change', check);

    return () => {};
  }, []);

  return [installed, setInstalled];
};

export const usePwaAutoInstall = () => {
  const [installed] = usePwaInstalled();
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as any);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  useEffect(() => {
    if (installed || !isMobile || !deferredPrompt) return;

    const checkAndPropose = async () => {
      const lastProposalDate = localStorage.getItem(LAST_INSTALL_PROPOSAL_KEY);
      const now = new Date().getTime();

      if (lastProposalDate) {
        const daysSinceLastProposal =
          (now - parseInt(lastProposalDate, 10)) / (1000 * 60 * 60 * 24);
        if (daysSinceLastProposal < DAYS_BETWEEN_PROPOSALS) {
          return;
        }
      }

      localStorage.setItem(LAST_INSTALL_PROPOSAL_KEY, String(now));
      installAppModal.open();
    };

    const timeoutId = setTimeout(checkAndPropose, 1000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [installed, deferredPrompt]);
};
